import React from 'react';
import {
  Heading,
  IconButton,
  InfoSignIcon,
  Pane,
  Popover,
  SeriesFilteredIcon,
  Strong,
  Text,
  Tooltip,
} from 'evergreen-ui';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import VirtualTable from 'Utils/CustomVirtualTalbe';
import { createUseStyles } from 'react-jss';
import format from 'date-fns/format';
import ReactPaginate from 'react-paginate';
import { UTCtoTimeZone } from 'Utils/UTCConverter';
import { INPUT_DATETIME, shouldBeValue } from 'Features/Reports/reportUtils';
import { useUrlParams } from 'Utils/useUrlParams';
import { usePagination } from 'Utils/Pagination/usePagination';
import { DATE_FORMATS } from 'Utils/formats';
import { ReportContext } from 'Features/Reports/Report.Context';
import { useSelector } from 'react-redux';
import { TimeseriesContext } from './Timeseries.Context';
import { useTableDataLoader } from './useTableDataLoader';
import { DIRTY_DATA_TABLE_TYPE, TIMESERIES_TABLE_TYPE, REPORT_DATA_TABLE_TYPE, SOURCE_TYPE_DATASOURCE, SOURCE_TYPE_STATION } from './Timeseries.Utils';
import TimeseriesChartsProvider, { TimeseriesChartsContext } from './Timeseries.Charts.Context';
import { timeZoneSelector } from '../../Features/App/appSlice';
import { EditTimeSeriesContext, EditTimeSeriesProvider } from './EditTimeseriesData/EditData.Context';

const useStyles = createUseStyles({
  hovered: {
    '&:hover > div': {
      opacity: 1,
    },
  },
  panel: {
    transition: 'all .2s',
    opacity: 0,
  },
  loader: {
    display: 'grid',
    justifyContent: 'center',
    padding: '2em',
  },
  cell: {
    position: 'relative',
    display: 'grid',
    alignItems: 'center',
    cursor: 'pointer',
    height: '100%',
    '&:hover': {
      background: 'rgb(201, 197, 197)',
    },
    backgroundClip: 'content-box',
  },
  infoWrapper: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    columnGap: '.2rem',
    alignSelf: 'flex-end',
    justifySelf: 'flex-end',
    position: 'absolute',
    right: 5,
    bottom: 5,
  },
  info: {
    backgroundColor: '#1070ca',
    width: 15,
    height: 15,
    borderRadius: 20,
    display: 'grid',
    alignContent: 'center',
  },
  cellWarning: {
    backgroundColor: '#fcf9ed',
  },
  cellDanger: {
    backgroundColor: '#fef6f6',
  },
  visibleWebsocketData: {
    animationName: 'onVisibleWebsocketData',
    animationDuration: '2s',
  },
  '@global': {
    '@keyframes onVisibleWebsocketData': {
      '0%': {
        backgroundColor: 'rgb(201, 197, 197)',
      },
      '100%': {
        backgroundColor: 'transparent',
      },
    },
  },
});

export default function TableWrapper() {
  const [, updateParams] = useUrlParams();
  const { date, pageSize, page } = React.useContext(TimeseriesContext);

  const { count, getDate, pageLabelBuilder } = usePagination(pageSize, date);

  const paginate = React.useMemo(() => {
    const period = getDate(count - page);
    if (count === null) return null;
    return {
      startDate: format(period.start, INPUT_DATETIME),
      endDate: format(period.end, INPUT_DATETIME),
    };
  }, [count, getDate, page]);

  const onPageChange = (e) => {
    const { selected } = e;
    updateParams({
      ...date,
      page: selected,
    });
  };

  return (
    <Pane display="grid" gridTemplateRows="1fr max-content" rowGap=".5rem">
      <EditTimeSeriesProvider>
        <TimeseriesChartsProvider paginate={paginate}>
          <Table paginate={paginate} />
        </TimeseriesChartsProvider>
      </EditTimeSeriesProvider>
      <ReactPaginate
        forcePage={page}
        previousLabel="пред."
        nextLabel="след."
        breakLabel="..."
        breakClassName="break-me"
        pageCount={count + 1}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={onPageChange}
        containerClassName="pagination"
        pageLabelBuilder={pageLabelBuilder}
        pageLinkClassName="pageLink"
        subContainerClassName="pages pagination"
        activeClassName="active"
      />
    </Pane>
  );
}

function HeaderRowDefault({ item, columnIndex }) {
  const classes = useStyles();
  const { tableType } = React.useContext(TimeseriesContext);
  const { setSelectedColumnIndex } = React.useContext(TimeseriesChartsContext);

  const onClickChart = () => setSelectedColumnIndex(columnIndex - 1);

  if (typeof item === 'string') {
    return <Heading fontSize={14} padding=".5rem">{item}</Heading>;
  }
  return (
    <Pane
      display="grid"
      height="100%"
      padding=".5em"
      gridTemplateColumns="1fr max-content"
      alignItems="center"
      justifyContent="center"
      position="relative"
      className={classes.hovered}
    >
      <Heading
        fontSize={14}
        display="grid"
        gridAutoFlow="row"
        gridAutoRows="max-content"
      >
        <Strong className="line-clamp">{`${item.label}${item.unit ? `, ${item.unit}` : ''}`}</Strong>
        {item.datasource && <Text>{item.datasource}</Text>}
        {item.stationEntity && item.stationEntity !== -1 && <Text>{item.entityName}</Text>}
        {item.placeName && <Text>{item.placeName}</Text>}
      </Heading>
      <Pane className={classes.panel} position="absolute" bottom={0} right={0} display="grid" gridAutoFlow="column">
        {tableType !== DIRTY_DATA_TABLE_TYPE && (
          <Tooltip content="График">
            <IconButton
              appearance="minimal"
              icon={SeriesFilteredIcon}
              onClick={onClickChart}
            />
          </Tooltip>
        )}
        <Popover
          content={(
            <Pane display="grid" padding=".5em">
              <Text fontSize={16}>
                <b>Метеокод: </b>
                {item.meteocode}
              </Text>
              <Text fontSize={16}>
                <b>Описание: </b>
                {item.description}
              </Text>
              <Text fontSize={16}>
                <b>Объект станции: </b>
                {item.entityName || '-'}
              </Text>
              <Text fontSize={16}>
                <b>Место расположения: </b>
                {item.placeName || '-'}
              </Text>
            </Pane>
          )}
        >
          <Tooltip content="Подробно">
            <IconButton appearance="minimal" icon={InfoSignIcon} />
          </Tooltip>
        </Popover>
      </Pane>
    </Pane>
  );
}
const HeaderRow = React.memo(HeaderRowDefault);
HeaderRow.displayName = 'HeaderRow';

function RowRenderDefault({ item, headers, rows, rowIndex, columnIndex }) {
  const { sourceType, tableType, zone, columns } = React.useContext(TimeseriesContext);
  const { setEditedValue } = React.useContext(EditTimeSeriesContext);
  const classes = useStyles();
  const selectedZone = useSelector(timeZoneSelector);

  const [value, setValue] = React.useState(item);

  React.useEffect(() => {
    if (item) {
      setValue(item);
    }
  }, [item]);

  const hasManyValues = React.useMemo(() => {
    if (typeof value !== 'string') {
      if (tableType !== DIRTY_DATA_TABLE_TYPE) {
        return value.values.length > 1;
      }
      return value.length > 1;
    }
    return false;
  }, [tableType, value]);

  if (typeof value === 'string') {
    return (
      <Text>
        {UTCtoTimeZone(value, selectedZone.selectedUserTimeZone, DATE_FORMATS.timeseries.withSecondsTable)}
      </Text>
    );
  }

  const cellSettings = () => {
    const setting = {
      style: '',
      tooltip: '',
    };
    if (value.hasWebsocketData) {
      setting.style = `${classes.visibleWebsocketData} `;
      const timeout = setTimeout(() => {
        setValue((cur) => ({
          ...cur,
          hasWebsocketData: false,
        }));
        // eslint-disable-next-line no-param-reassign
        item.hasWebsocketData = false;
        clearTimeout(timeout);
      }, 1000);
    }

    if (value.shouldBeValues === shouldBeValue.WARNING) {
      setting.style += classes.cellWarning;
      setting.tooltip = `Данные в срок${hasManyValues ? '\nМножество данных' : ''}`;
    } else if (value.shouldBeValues === shouldBeValue.DANGER) {
      setting.style += classes.cellDanger;
      setting.tooltip = `Обязательные данные в срок${hasManyValues ? '\nМножество данных' : ''}`;
    }

    return setting;
  };

  const onClickCell = () => {
    if (tableType !== DIRTY_DATA_TABLE_TYPE) {
      const metadata = {
        indexes: {
          rowIndex,
          columnIndex,
        },
        columnData: headers,
        dateTime: rows[0],
        values: (tableType === TIMESERIES_TABLE_TYPE || tableType === REPORT_DATA_TABLE_TYPE) ? value.values : value,
        dataType: sourceType,
      };
      if (tableType === REPORT_DATA_TABLE_TYPE) {
        const column = columns[columnIndex - 1];
        metadata.dataType = column.sourceType;
        metadata.sourceId = column.sourceId;
      }
      setEditedValue(metadata);
    }
  };

  const tableCellValue = tableType === DIRTY_DATA_TABLE_TYPE ? value[0] : value.values[0];

  const existComment = (tableType === TIMESERIES_TABLE_TYPE || tableType === REPORT_DATA_TABLE_TYPE) && item.values.some((val) => val.dataValueComment !== null);
  const settings = cellSettings();

  return (
    <Pane className={`${classes.cell} ${settings.style}`} title={settings.tooltip} padding={1} onClick={onClickCell}>
      <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" justifyContent="center">
        {hasManyValues ? <Text>[...]</Text> : <Text>{tableCellValue && (tableCellValue.valueRound || tableCellValue.value)}</Text>}
      </Pane>
      <Pane className={classes.infoWrapper}>
        {value.values.length > 0 && tableCellValue && tableCellValue.valueType !== 'REAL' && (
          <Tooltip content="Автоматически расчитанные данные">
            <Pane className={classes.info}>
              <Text color="white" fontSize={10}>A</Text>
            </Pane>
          </Tooltip>
        )}
        {
          existComment && (
            <Tooltip content="Одно из значений имеет комментарий пользователя">
              <Pane className={classes.info}>
                <Text color="white" fontSize={10}>К</Text>
              </Pane>
            </Tooltip>
          )
        }
      </Pane>
    </Pane>
  );
}
const RowRender = React.memo(RowRenderDefault);
RowRender.displayName = 'RowRender';

function Table({ paginate }) {
  const selectedZone = useSelector(timeZoneSelector);

  const { allMeteocodes, meteocodes, tableType, columns } = React.useContext(TimeseriesContext);
  const { tableData, isLoadingData, loadMoreData, isShowSpinner, realColumns } = useTableDataLoader(paginate);

  // TODO: изменить формирование шапки, если выбрана программа
  const headers = React.useMemo(() => {
    const tmp = [];

    if (tableType === REPORT_DATA_TABLE_TYPE) {
      columns.forEach((column) => {
        if (realColumns === undefined) {
          return
        }
        let correspondingColumns = realColumns[column.meteocode]
        if (correspondingColumns === undefined) {
          return
        }

        correspondingColumns.forEach(real => {
          if (
              (real.stationEntityId === -1 && column.stationEntityId === undefined)
              || (column.stationEntityId !== undefined && column.stationEntityId === real.stationEntityId)
          ) {
            const header = {
              label: 'Нет описания',
              stationEntity: column.stationEntity?.id,
              meteocode: column.meteocode,
              entityName: column.stationEntity?.entityName,
              datasource: real.datasourceName,
              placeName: column.place || '',
              unit: '',
            };
            const meteocode = allMeteocodes.find((mc) => mc.meteoCode === column.meteocode);
            if (meteocode) {
              header.label = meteocode.meteoCodeName;
              header.description = meteocode.meteoCodeDescription;
              header.unit = meteocode.unit;
            }
            tmp.push(header);
          }
        })
      });
    } else if (meteocodes.length > 0) {
      meteocodes.forEach((meteocode) => {
        if (realColumns === undefined) {
          return
        }
        let correspondingColumns = realColumns[meteocode.meteoCode]
        if (correspondingColumns === undefined) {
          return
        }
        correspondingColumns.forEach(real => {
          if (
              (real.stationEntityId === -1 && meteocode.entityId === undefined)
              || (meteocode.entityId !== undefined && meteocode.entityId === real.stationEntityId)
          ) {
            tmp.push({
              meteocode: meteocode.meteoCode,
              label: meteocode.meteoCodeName,
              entityName: meteocode.entityName,
              stationEntity: meteocode.entityId,
              description: meteocode.meteoCodeDescription,
              datasource: real.datasourceName,
              unit: tableType !== DIRTY_DATA_TABLE_TYPE ? meteocode.unit : '',
              placeName: meteocode.placeName,
            })
          }
        })
      });
    }
    return [`Дата UTC${selectedZone.selectedUserTimeZone}`].concat(tmp);
  }, [allMeteocodes, columns, meteocodes, tableType, selectedZone, realColumns]);

  const Loader = React.useCallback(() => (
    <div>
      <SuspenseSpinner />
    </div>
  ), []);

  if (tableType !== REPORT_DATA_TABLE_TYPE && meteocodes.length === 0) {
    return (
      <Pane alignSelf="center" justifySelf="center" display="grid" rowGap=".5em">
        <Heading fontSize={24}>Не выбраны измерения или они отсутствуют у источника!</Heading>
      </Pane>
    );
  }

  if (isShowSpinner && isLoadingData) return <SuspenseSpinner />;

  return (
    <VirtualTable
      tableData={tableData}
      header={headers}
      headerRowHeight={70}
      HeaderRender={HeaderRow}
      RowRender={RowRender}
      columnWidth={250}
      rowHeight={40}
      fixedColumnCount={1}
      onFetch={loadMoreData}
      isFetch={isLoadingData && !isShowSpinner}
      Loader={Loader}
      TableLegend={tableType === REPORT_DATA_TABLE_TYPE ? ReportLegend : null}
    />
  );
}

function ReportLegend({ firstColumnIndex, visibleColumns }) {
  const { groups, posts } = React.useContext(ReportContext);

  const legends = React.useMemo(() => {
    const lgnd = {
      posts: [],
      subPosts: [],
    };
    groups.forEach((group) => {
      const [post, columns] = group;
      columns.forEach((column) => {
        const subPost = posts.find((p) => {
          const isDatasource = column.sourceType === SOURCE_TYPE_DATASOURCE;
          if (isDatasource) {
            return p.id === column.sourceId && !!p.stationIdx;
          }
          return p.id === column.sourceId && !p.stationIdx && p.stationDatasourceName === column.stationDatasourceName;
        });
        lgnd.posts.push({
          label: post.name,
          color: post.options.color,
          subPostsLengh: columns.length,
        });
        if (subPost) {
          lgnd.subPosts.push({
            label: subPost.shortName ? `${subPost.shortName}` : subPost.name,
            idx: subPost.idx || subPost.stationIdx,
            id: subPost.id,
            type: subPost.stationIdx ? SOURCE_TYPE_DATASOURCE : SOURCE_TYPE_STATION,
            station: subPost.station,
            stationDatasourceName: subPost.stationDatasourceName
          });
        }
      });
    });
    return lgnd;
  }, [groups, posts]);

  const slicedData = React.useMemo(() => ({
    posts: legends.posts.reduce((acc, item) => {
      if (!acc.find((p) => p.label === item.label)) {
        acc.push(item);
      }
      return acc;
    }, []).map((post, index, arr) => {
      let colSpan = post.subPostsLengh;
      const startIndex = arr.slice(0, index).reduce((acc, item) => acc + item.subPostsLengh * index, 0);
      if (startIndex < firstColumnIndex) {
        colSpan = post.subPostsLengh - (firstColumnIndex - startIndex);
      }
      return {
        ...post,
        startIndex,
        colSpan,
      };
    }),
    subPosts: legends.subPosts.slice(firstColumnIndex, visibleColumns + firstColumnIndex + 1),
  }), [firstColumnIndex, legends.posts, legends.subPosts, visibleColumns]);

  function showStationDatasourceRow(postLegend) {
    if (
        postLegend.type === SOURCE_TYPE_STATION
        && postLegend.stationDatasourceName !== null
        && postLegend.stationDatasourceName !== undefined
    ) {
      return <Strong>{`${postLegend.stationDatasourceName}`}</Strong>
    }
    return <div></div>
  }

  return (
    <>
      <tr>
        <th rowSpan={2}> </th>
        {
          React.Children.toArray(slicedData.posts.map((postLegend) => (
            <>
              {postLegend.colSpan > 0 && (
                <th colSpan={postLegend.colSpan > visibleColumns ? visibleColumns : postLegend.colSpan} style={{ padding: 0 }}>
                  <Pane height="100%" borderBottom={`5px solid ${postLegend.color}`} padding={8}>
                    <Strong>{postLegend.label}</Strong>
                  </Pane>
                </th>
              )}
            </>
          )))
        }
      </tr>
      <tr>
        {
          React.Children.toArray(slicedData.subPosts.map((postLegend) => (
            <th style={{ padding: 0 }}>
              <Pane height="100%" display="grid" gridAutoFlow="row" gridAutoRows="max-content">
                <Strong>{postLegend.type === SOURCE_TYPE_STATION ? `Станция idx: ${postLegend.idx}` : `Источник ${postLegend.label} id: ${postLegend.id}`}</Strong>
                <Strong>{`${postLegend.type === SOURCE_TYPE_STATION ? postLegend.label : postLegend.station}`}</Strong>
                {showStationDatasourceRow(postLegend)}
              </Pane>
            </th>
          )))
        }
      </tr>
    </>
  );
}
